import React, { useState, useEffect } from "react";
import { Button, Divider, Input, Upload } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";

const PaymentTermsPolicyEditor = ({
  slide,
  updateSlide,
  removeSlide,
  triggerSave,
}) => {
  const [policyGroups, setPolicyGroups] = useState(
    slide.policyGroups || [{ groupName: "Policy Section 01", detail: [] }]
  );
  const [background, setBackground] = useState(slide.bg || "");
  const [heading, setHeading] = useState(
    slide.heading || "Payment TERMS & POLICY"
  );
  const [tagline, setTagline] = useState(slide.tagline || "");

  useEffect(() => {
    setPolicyGroups(
      slide.policyGroups || [{ groupName: "Policy Section 01", detail: [] }]
    );
    setBackground(slide.bg || "");
    setHeading(slide.heading || "Payment TERMS & POLICY");
    setTagline(slide.tagline || "");
  }, [slide]);

  useEffect(() => {
    if (triggerSave) {
      updateSlide(slide.id, {
        policyGroups,
        heading,
        tagline,
        bg: background,
      });
    }
  }, [triggerSave]);

  const handleBackgroundChange = (info) => {
    const file = info.file.originFileObj || info.file;
    if (file && file instanceof Blob) {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        setBackground(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const addPolicyGroup = () => {
    const newGroup = {
      groupName: `Policy Section ${policyGroups.length + 1}`,
      detail: [],
    };
    setPolicyGroups([...policyGroups, newGroup]);
  };

  const removeGroup = (groupIndex) => {
    setPolicyGroups(policyGroups.filter((_, idx) => idx !== groupIndex));
  };

  const addDetail = (groupIndex, type = "object") => {
    setPolicyGroups((prev) =>
      prev.map((g, idx) =>
        idx === groupIndex
          ? {
              ...g,
              detail: [
                ...g.detail,
                type === "string" ? "" : { heading: "", description: "" },
              ],
            }
          : g
      )
    );
  };

  const updateStringDetail = (groupIndex, detailIndex, value) => {
    setPolicyGroups((prev) =>
      prev.map((g, gIdx) =>
        gIdx === groupIndex
          ? {
              ...g,
              detail: g.detail.map((d, dIdx) =>
                dIdx === detailIndex ? value : d
              ),
            }
          : g
      )
    );
  };

  const updateObjectDetail = (groupIndex, detailIndex, field, value) => {
    setPolicyGroups((prev) =>
      prev.map((g, gIdx) =>
        gIdx === groupIndex
          ? {
              ...g,
              detail: g.detail.map((d, dIdx) =>
                dIdx === detailIndex ? { ...d, [field]: value } : d
              ),
            }
          : g
      )
    );
  };

  const removeDetail = (groupIndex, detailIndex) => {
    setPolicyGroups((prev) =>
      prev.map((g, gIdx) =>
        gIdx === groupIndex
          ? {
              ...g,
              detail: g.detail.filter((_, dIdx) => dIdx !== detailIndex),
            }
          : g
      )
    );
  };

  return (
    <div className="border p-3 rounded bg-white shadow-sm">
      <Divider />
      <h1 className="font-semibold mb-4 text-lg">
        Edit Payment Terms Policy Slide
      </h1>

      {/* Background */}
      <div className="mb-4">
        <Upload
          accept="image/*"
          beforeUpload={() => false}
          showUploadList={false}
          onChange={handleBackgroundChange}
        >
          <Button type="primary" icon={<UploadOutlined />}>
            Upload Background
          </Button>
        </Upload>
        {background && (
          <img
            src={background}
            alt="Background Preview"
            className="block mt-2 w-full h-32 max-w-xs max-h-xs object-cover rounded-lg border"
          />
        )}
      </div>

    
      <div className="mb-4">
        <label className="font-medium">Slide Heading</label>
        <Input value={heading} onChange={(e) => setHeading(e.target.value)} />
      </div>

      {policyGroups.map((group, gIdx) => (
        <div key={gIdx} className="mb-6 border p-3 rounded">
          <Input
            placeholder="Group Name"
            value={group.groupName}
            className="mb-2"
            onChange={(e) => {
              const newGroups = [...policyGroups];
              newGroups[gIdx].groupName = e.target.value;
              setPolicyGroups(newGroups);
            }}
          />

          {group.detail.map((item, dIdx) => (
            <div
              key={dIdx}
              className="border p-2 rounded mb-3 bg-gray-50 flex flex-col gap-2"
            >
              {typeof item === "string" ? (
                <Input.TextArea
                  placeholder="Policy Text"
                  className="mb-2"
                  value={item}
                  rows={2}
                  onChange={(e) =>
                    updateStringDetail(gIdx, dIdx, e.target.value)
                  }
                />
              ) : (
                <>
                  <Input
                    placeholder="Heading"
                    className="mb-2"
                    value={item.heading}
                    onChange={(e) =>
                      updateObjectDetail(gIdx, dIdx, "heading", e.target.value)
                    }
                  />
                  <Input.TextArea
                    placeholder="Description"
                    className="mb-2"
                    value={item.description}
                    rows={3}
                    onChange={(e) =>
                      updateObjectDetail(
                        gIdx,
                        dIdx,
                        "description",
                        e.target.value
                      )
                    }
                  />
                </>
              )}
              <Button
                danger
                icon={<DeleteOutlined />}
                className=""
                onClick={() => removeDetail(gIdx, dIdx)}
              >
                Remove Detail
              </Button>
            </div>
          ))}

          {/* Add detail buttons */}
          <div className="flex gap-2 mb-2">
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => addDetail(gIdx, "object")}
            >
              Add Heading + Description
            </Button>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => addDetail(gIdx, "string")}
            >
              Add Text Term
            </Button>
          </div>

          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeGroup(gIdx)}
            className="mb-4"
          >
            Remove Group
          </Button>
        </div>
      ))}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addPolicyGroup}
        className="mb-4"
      >
        Add Policy Section
      </Button>

      <div className="mt-6 flex justify-end">
        <Button danger type="primary" onClick={() => removeSlide(slide.id)}>
          <DeleteOutlined /> Remove Slide
        </Button>
      </div>
    </div>
  );
};

export default PaymentTermsPolicyEditor;
